<template>
    <div class="row">
        <div v-for="table in tables" :key="`${table.type}-${table.id}`" class="col-12 col-sm-6 col-lg-3 mb-4">
            <div class="table-qr-card h-100" :class="{ selected: isSelected(table), selectable: selectionMode }" @click="$emit('card-click', table)">
                <div class="card-content">
                    <div class="card-header-section">
                        <div>
                            <h4 class="table-title">{{ getCardTitle(table) }}</h4>
                            <div v-if="table.type === 'table'" class="card-meta">
                                <span class="location-badge" :class="{ unlinked: !table.room_id }">
                                    {{ getTableLocation(table) }}
                                </span>
                                <span class="seats-badge">{{ table.seats }} couverts</span>
                            </div>
                        </div>
                        <div class="qr-icon" :class="{ active: table.url }">
                            <i class="fa fa-qrcode"></i>
                        </div>
                    </div>

                    <div class="mb-3">
                        <div v-if="table.url" class="status-text success">
                            <i class="fa fa-circle me-1"></i>{{ $tl("labels.booking.seatingPlan.qrCode.generated") }}
                        </div>
                        <div v-else class="status-text pending">
                            <i class="fa fa-circle me-1"></i>{{ $tl("labels.booking.seatingPlan.qrCode.pending") }}
                        </div>
                    </div>

                    <div class="mb-3">
                        <div v-if="table.url" class="w-100">
                            <div class="d-flex" style="gap: 8px; margin-bottom: 8px">
                                <button
                                    type="button"
                                    class="btn btn-outline-dark px-3 py-2 btn-sm flex-fill"
                                    @click.stop="selectionMode ? null : $emit('preview', table)">
                                    <i class="fa fa-eye me-1"></i>{{ $tl("labels.booking.seatingPlan.qrCode.view") }}
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-outline-dark px-3 py-2 btn-sm flex-fill"
                                    @click.stop="selectionMode ? null : $emit('print', table)">
                                    <i class="fa fa-print me-1"></i>{{ $tl("labels.booking.seatingPlan.qrCode.print") }}
                                </button>
                            </div>
                            <button
                                type="button"
                                class="btn-regenerate px-0 py-2"
                                :disabled="isGenerating(table.id)"
                                @click.stop="selectionMode ? null : $emit('regenerate', table)">
                                <i class="fa fa-sync-alt me-1"></i>{{ $tl("labels.booking.seatingPlan.qrCode.regenerate") }}
                            </button>
                        </div>

                        <div v-else>
                            <button
                                type="button"
                                class="btn btn-primary d-block w-100"
                                :disabled="isGenerating(table.id)"
                                @click.stop="selectionMode ? null : $emit('generate', table)">
                                <i class="fa fa-qrcode me-2"></i>
                                {{ $tl("labels.booking.seatingPlan.qrCode.generate") }}
                            </button>
                        </div>
                    </div>
                    <div class="widget-actions">
                        <button type="button" class="widget-preview-button"
                            :disabled="selectionMode || isOpeningWidget(table, 'preview')"
                            @click.stop="$emit('open-widget', table)">
                            <i :class="isOpeningWidget(table, 'preview') ? 'fa fa-spinner fa-spin' : 'fa fa-eye'" aria-hidden="true"></i>
                            Voir mon widget
                        </button>
                        <p>Aperçu libre · commandes simulées</p>
                        <button type="button" class="widget-preview-button widget-preview-button--qr"
                            :disabled="selectionMode || isOpeningWidget(table, 'qr')"
                            @click.stop="$emit('test-qr', table)">
                            <i :class="isOpeningWidget(table, 'qr') ? 'fa fa-spinner fa-spin' : 'fa fa-qrcode'" aria-hidden="true"></i>
                            Tester un QR code
                        </button>
                        <p>Règles QR, commandes et paiements réels</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "OrderingTableQrCardsGrid",
    props: {
        tables: {
            type: Array,
            required: true,
        },
        selectionMode: {
            type: Boolean,
            required: true,
        },
        selectedTableKeys: {
            type: Array,
            required: true,
        },
        statuses: {
            type: Object,
            required: true,
        },
        widgetStatuses: {
            type: Object,
            default: () => ({}),
        },
    },
    methods: {
        tableKey(table) {
            return `${table.type}:${table.id}`;
        },
        isSelected(table) {
            return this.selectedTableKeys.includes(this.tableKey(table));
        },
        isGenerating(tableId) {
            return !!(this.statuses[tableId] && this.statuses[tableId].generating);
        },
        isOpeningWidget(table, mode) {
            return Boolean(this.widgetStatuses[`${this.tableKey(table)}:${mode}`]);
        },
        getCardTitle(table) {
            if (table.type === "generic") {
                return table.name;
            }

            return `${this.$tl("labels.booking.seatingPlan.table.title")} ${table.name}`;
        },
        getTableLocation(table) {
            if (!table.room_id) {
                return "Non liee";
            }

            if (table.seating_plan_name && table.room_name) {
                return `${table.seating_plan_name} / ${table.room_name}`;
            }

            return table.room_name || table.seating_plan_name || "Salle liee";
        },
    },
};
</script>

<style scoped lang="scss">
.table-qr-card {
    background: #fff;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
    border: 3px solid transparent;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    &.selectable {
        cursor: pointer;

        &:hover {
            border-color: rgba(48, 164, 183, 0.3);
        }
    }

    &.selected {
        border-color: #30a4b7 !important;
        box-shadow: 0 4px 16px rgba(48, 164, 183, 0.3);
    }
}

.card-content {
    padding: 20px;
}

.card-header-section {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
}

.table-title {
    font-size: 20px;
    font-weight: 600;
    color: #111827;
    margin: 0;
}

.card-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 10px;
}

.location-badge,
.seats-badge {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 4px 10px;
    font-size: 12px;
    font-weight: 600;
}

.location-badge {
    background: #e0f2fe;
    color: #0f766e;

    &.unlinked {
        background: #fef3c7;
        color: #92400e;
    }
}

.seats-badge {
    background: #f3f4f6;
    color: #374151;
}

.qr-icon {
    width: 40px;
    height: 40px;
    background: #f3f4f6;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9ca3af;
    font-size: 18px;

    &.active {
        background: #d1fae5;
        color: #059669;
    }
}

.status-text {
    font-size: 14px;
    font-weight: 500;
    display: flex;
    align-items: center;

    &.success {
        color: #059669;

        i.fa-circle {
            font-size: 8px;
        }
    }

    &.pending {
        color: #9ca3af;

        i.fa-circle {
            font-size: 8px;
        }
    }
}

.btn-outline-dark {
    border: 1px solid #e5e7eb;
    background: #fff;
    color: #374151;
    font-size: 14px;
    font-weight: 500;

    &:hover {
        background: #f9fafb;
        border-color: #d1d5db;
    }
}

.widget-preview-button {
    width: 100%;
    min-height: 44px;
    border: 1px solid rgba(48, 164, 183, 0.24);
    border-radius: 999px;
    background: #eefbff;
    color: #176b7a;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    font-size: 14px;
    font-weight: 600;
    transition:
        background 0.2s ease,
        border-color 0.2s ease,
        box-shadow 0.2s ease,
        transform 0.2s ease;

    &:hover:not(:disabled) {
        background: #e2f7fc;
        border-color: rgba(48, 164, 183, 0.48);
        box-shadow: 0 6px 14px rgba(23, 107, 122, 0.12);
        transform: translateY(-1px);
    }

    &:disabled {
        opacity: 0.65;
        cursor: wait;
        transform: none;
        box-shadow: none;
    }
}

.widget-actions { padding-top: 16px; border-top: 1px solid #e3eceb; }
.widget-actions p { margin: 6px 0 14px; font-size: 12px; text-align: center; color: #52676a; }
.widget-actions p:last-child { margin-bottom: 0; }
.widget-preview-button--qr { background: #fff; }
.widget-preview-button:focus-visible { outline: 2px solid #176b7a; outline-offset: 3px; }

.btn-regenerate {
    background: none;
    border: none;
    color: #6b7280;
    font-size: 14px;
    font-weight: 500;
    width: 100%;
    text-align: center;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
        color: #374151;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

.btn-primary {
    border: none;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    padding: 12px 20px;
    border-radius: 8px;
    transition: all 0.2s ease;

    &:hover {
        transform: translateY(-1px);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
}
</style>
